import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const PushNotificationContext = createContext(null);

export const usePushNotification = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotification must be used within PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      registerServiceWorker();
    }
  }, []);

  // Register service worker
  const registerServiceWorker = async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw-push.js');
      console.log('[Push] Service Worker registered:', reg);
      setRegistration(reg);
      
      // Check for updates
      reg.addEventListener('updatefound', () => {
        console.log('[Push] Service Worker update found');
      });
    } catch (error) {
      console.error('[Push] Service Worker registration failed:', error);
    }
  };

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Push notifications enabled!');
        return true;
      } else if (result === 'denied') {
        toast.error('Push notifications blocked. Please enable in browser settings.');
        return false;
      }
      return false;
    } catch (error) {
      console.error('[Push] Permission request failed:', error);
      toast.error('Failed to request notification permission');
      return false;
    }
  }, [isSupported]);

  // Show a local notification (browser notification)
  const showNotification = useCallback(async (title, options = {}) => {
    if (!isSupported) return false;
    
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return false;
    }

    try {
      // Use service worker if available, otherwise fallback to Notification API
      if (registration) {
        await registration.showNotification(title, {
          body: options.body || '',
          icon: options.icon || '/logo192.png',
          badge: '/logo192.png',
          tag: options.tag || `notification-${Date.now()}`,
          vibrate: [200, 100, 200],
          data: options.data || {},
          requireInteraction: options.requireInteraction || false,
          actions: options.actions || []
        });
      } else {
        new Notification(title, {
          body: options.body || '',
          icon: options.icon || '/logo192.png',
          tag: options.tag || `notification-${Date.now()}`
        });
      }
      return true;
    } catch (error) {
      console.error('[Push] Failed to show notification:', error);
      return false;
    }
  }, [isSupported, permission, registration, requestPermission]);

  // Show enquiry notification
  const notifyNewEnquiry = useCallback((enquiry) => {
    const isAurousOrbit = enquiry.source === 'aurousorbit.ai';
    const sourceLabel = isAurousOrbit ? '🚀 AurousOrbit.ai' : '🌐 Website';
    
    showNotification(`New Enquiry from ${sourceLabel}`, {
      body: `${enquiry.name}: ${enquiry.subject}`,
      icon: '/logo192.png',
      tag: `enquiry-${enquiry.id}`,
      requireInteraction: true,
      data: {
        type: 'enquiry',
        id: enquiry.id,
        url: '/admin/enquiries',
        source: enquiry.source
      }
    });

    // Also show in-app toast
    toast.info(
      <div className="flex flex-col">
        <span className="font-semibold">{sourceLabel} Enquiry</span>
        <span className="text-sm">{enquiry.name}: {enquiry.subject}</span>
      </div>,
      { duration: 8000 }
    );
  }, [showNotification]);

  // Show chat notification
  const notifyNewChat = useCallback((conversation) => {
    showNotification('New Live Chat Message', {
      body: `${conversation.customer_name}: ${conversation.last_message || 'Started a conversation'}`,
      icon: '/logo192.png',
      tag: `chat-${conversation.id}`,
      requireInteraction: true,
      data: {
        type: 'chat',
        id: conversation.id,
        url: '/admin/live-chat'
      }
    });

    // Also show in-app toast
    toast.info(
      <div className="flex flex-col">
        <span className="font-semibold">💬 New Chat Message</span>
        <span className="text-sm">{conversation.customer_name}</span>
      </div>,
      { duration: 8000 }
    );
  }, [showNotification]);

  const value = {
    permission,
    isSupported,
    requestPermission,
    showNotification,
    notifyNewEnquiry,
    notifyNewChat
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};
